import { useEffect, useRef, useState } from 'react';
import { UploadSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { mktApi, errMsg } from './marketingApi';

export default function MarketingSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoVer, setLogoVer] = useState(0);
  const [sealVer, setSealVer] = useState(0);
  const fileRef = useRef(null);
  const sealRef = useRef(null);

  useEffect(() => { mktApi.settings().then(setS).catch((e) => toast.error(errMsg(e))); }, []);

  if (!isAdmin(user)) return <p className="py-16 text-center text-sm text-zinc-400">このページは管理者のみ利用できます</p>;
  if (!s) return <p className="py-16 text-center text-sm text-zinc-400">読み込み中…</p>;

  const setCo = (k, v) => setS((p) => ({ ...p, company: { ...p.company, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await mktApi.saveSettings({
        default_margin_pct: Number(s.default_margin_pct) || 0,
        tax_rate: Number(s.tax_rate) || 0,
        min_margin_pct: Number(s.min_margin_pct) || 0,
        quote_valid_days: Number(s.quote_valid_days) || 30,
        company: s.company,
      });
      toast.success('設定を保存しました');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      await mktApi.uploadLogo(file);
      setS((p) => ({ ...p, logo_file_id: 'set' }));
      setLogoVer((v) => v + 1);
      toast.success('ロゴをアップロードしました');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadSeal = async (file) => {
    if (!file) return;
    try {
      await mktApi.uploadSeal(file);
      setS((p) => ({ ...p, seal_file_id: 'set' }));
      setSealVer((v) => v + 1);
      toast.success('社印をアップロードしました');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const co = s.company || {};
  const field = (label, k, ph = '') => (
    <div key={k}>
      <label className="text-xs font-medium text-zinc-600">{label}</label>
      <Input value={co[k] ?? ''} onChange={(e) => setCo(k, e.target.value)} placeholder={ph} className="mt-1 h-9" data-testid={`mkt-settings-co-${k}`} />
    </div>
  );

  return (
    <div className="space-y-5 max-w-3xl" data-testid="mkt-settings-page">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900">受注設定</h1>
        <p className="text-xs text-zinc-500 mt-0.5">見積書・請求書に印字される会社情報と、見積の既定値を管理します</p>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-4">
        <h3 className="text-sm font-semibold text-zinc-800">会社情報（書類に印字）</h3>
        <div className="flex items-center gap-4">
          <div className="h-16 w-44 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
            {s.logo_file_id
              ? <img src={`${mktApi.logoUrl}?v=${logoVer}`} alt="logo" className="max-h-14 max-w-40 object-contain" data-testid="mkt-settings-logo-preview" />
              : <span className="text-[10px] text-zinc-400">ロゴ未設定</span>}
          </div>
          <div className="space-x-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="mkt-settings-logo-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="mkt-settings-logo-upload-btn">
              <UploadSimple size={14} className="mr-1" /> ロゴをアップロード
            </Button>
            {s.logo_file_id && (
              <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                onClick={async () => { try { await mktApi.deleteLogo(); setS((p) => ({ ...p, logo_file_id: null })); toast.success('ロゴを削除しました'); } catch (e) { toast.error(errMsg(e)); } }}
                data-testid="mkt-settings-logo-delete-btn">
                <Trash size={14} className="mr-1" /> 削除
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 border-t border-zinc-200 pt-4">
          <div className="h-16 w-16 shrink-0 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
            {s.seal_file_id
              ? <img src={`${mktApi.sealUrl}?v=${sealVer}`} alt="社印" className="max-h-14 max-w-14 object-contain" data-testid="mkt-settings-seal-preview" />
              : <span className="text-[10px] text-zinc-400 text-center leading-tight">社印<br />未設定</span>}
          </div>
          <div>
            <div className="space-x-2">
              <input ref={sealRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadSeal(e.target.files?.[0])} data-testid="mkt-settings-seal-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => sealRef.current?.click()} data-testid="mkt-settings-seal-upload-btn">
                <UploadSimple size={14} className="mr-1" /> 社印をアップロード
              </Button>
              {s.seal_file_id && (
                <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                  onClick={async () => { try { await mktApi.deleteSeal(); setS((p) => ({ ...p, seal_file_id: null })); toast.success('社印を削除しました'); } catch (e) { toast.error(errMsg(e)); } }}
                  data-testid="mkt-settings-seal-delete-btn">
                  <Trash size={14} className="mr-1" /> 削除
                </Button>
              )}
            </div>
            <p className="mt-1.5 text-[10px] text-zinc-400">見積書・請求書の社名に重ねて印字されます（背景透過PNG推奨・4MBまで）</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field('会社名', 'name')}
          {field('担当部署・担当者', 'contact')}
          {field('郵便番号', 'zip', '123-4567')}
          {field('住所', 'address')}
          {field('TEL', 'tel')}
          {field('FAX', 'fax')}
          {field('インボイス登録番号', 'invoice_reg_no', 'T1234567890123')}
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">振込先（請求書に印字 / 改行可）</label>
          <Textarea value={co.bank ?? ''} onChange={(e) => setCo('bank', e.target.value)} rows={2}
            placeholder={'○○銀行 △△支店 普通 1234567\nカ）センスビー'} className="mt-1" data-testid="mkt-settings-co-bank" />
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-800">見積の既定値</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">既定マージン率 (%)</label>
            <Input type="number" step="any" value={s.default_margin_pct} onChange={(e) => setS((p) => ({ ...p, default_margin_pct: e.target.value }))} className="mt-1 h-9 text-right" data-testid="mkt-settings-margin" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">消費税率 (%)</label>
            <Input type="number" step="any" value={s.tax_rate} onChange={(e) => setS((p) => ({ ...p, tax_rate: e.target.value }))} className="mt-1 h-9 text-right" data-testid="mkt-settings-tax" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">最低マージン率 (%)</label>
            <Input type="number" step="any" value={s.min_margin_pct} onChange={(e) => setS((p) => ({ ...p, min_margin_pct: e.target.value }))} className="mt-1 h-9 text-right" data-testid="mkt-settings-min-margin" />
            <p className="mt-0.5 text-[10px] text-zinc-400">下回ると上長承認が必要</p>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">見積有効日数</label>
            <Input type="number" value={s.quote_valid_days} onChange={(e) => setS((p) => ({ ...p, quote_valid_days: e.target.value }))} className="mt-1 h-9 text-right" data-testid="mkt-settings-valid-days" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-settings-save-btn">
          {saving ? '保存中…' : '設定を保存'}
        </Button>
      </div>

    </div>
  );
}
